import config from 'config/index';

/**
 * URL Patterns Supported:
 *
 * 1. App Domain Pattern: {tenant}.{app-domain}/supplier
 *    Example: acme.tendo.one/supplier
 *    - Tenant slug extracted from subdomain
 *
 * 2. Custom Domain Pattern: supplier.{tenant-custom-domain}
 *    Example: supplier.acme-corp.com
 *    - Tenant identified by the custom domain (not subdomain)
 *    - Backend must resolve custom domain to tenant
 */

/**
 * Extracts the subdomain from a given hostname
 * @param hostname - The hostname to extract subdomain from (e.g., 'acme.tendo.one')
 * @param baseDomain - The base domain (e.g., 'tendo.one')
 * @returns The subdomain or null if none exists
 *
 * Examples:
 * - extractSubdomain('acme.tendo.one', 'tendo.one') => 'acme'
 * - extractSubdomain('app.acme.tendo.one', 'tendo.one') => 'app.acme'
 * - extractSubdomain('tendo.one', 'tendo.one') => null
 * - extractSubdomain('localhost', 'tendo.one') => null
 */
export const extractSubdomain = (
  hostname: string,
  baseDomain: string
): string | null => {
  // Remove www. prefix if present
  const cleanHostname = hostname.replace(/^www\./, '');
  const cleanBaseDomain = baseDomain.replace(/^www\./, '');

  // If hostname doesn't end with base domain, no subdomain from app domain
  if (!cleanHostname.endsWith(cleanBaseDomain)) {
    return null;
  }

  // If hostname equals base domain, no subdomain
  if (cleanHostname === cleanBaseDomain) {
    return null;
  }

  // Extract subdomain by removing base domain and trailing dot
  const subdomain = cleanHostname.replace(`.${cleanBaseDomain}`, '');

  return subdomain || null;
};

/**
 * Checks if the current hostname is a custom domain (not the app domain)
 * @param hostname - The hostname to check
 * @param baseDomain - The base app domain (e.g., 'tendo.one')
 * @returns true if this is a custom tenant domain
 *
 * Examples:
 * - isCustomDomain('acme.tendo.one', 'tendo.one') => false
 * - isCustomDomain('supplier.acme-corp.com', 'tendo.one') => true
 * - isCustomDomain('localhost', 'tendo.one') => false
 */
export const isCustomDomain = (hostname: string, baseDomain: string): boolean => {
  const cleanHostname = hostname.replace(/^www\./, '').split(':')[0];
  const cleanBaseDomain = baseDomain.replace(/^www\./, '');

  // Localhost is not a custom domain
  if (
    cleanHostname === 'localhost' ||
    cleanHostname === '127.0.0.1' ||
    cleanHostname.startsWith('192.168.') ||
    cleanHostname.endsWith('.local')
  ) {
    return false;
  }

  // If it doesn't end with the app domain, it's a custom domain
  return !cleanHostname.endsWith(cleanBaseDomain);
};

/**
 * Extracts the custom domain (without 'supplier.' prefix if present)
 * @param hostname - The hostname (e.g., 'supplier.acme-corp.com')
 * @returns The custom domain (e.g., 'acme-corp.com')
 */
export const extractCustomDomain = (hostname: string): string => {
  const cleanHostname = hostname.replace(/^www\./, '').split(':')[0];

  // Remove 'supplier.' prefix if present
  if (cleanHostname.startsWith('supplier.')) {
    return cleanHostname.replace('supplier.', '');
  }

  return cleanHostname;
};

/**
 * Gets tenant identification from the current window location
 * Falls back to query parameter 'tenant' for local development
 *
 * Returns an object with:
 * - slug: The tenant slug (for app domain pattern)
 * - customDomain: The custom domain (for custom domain pattern)
 * - type: 'subdomain' | 'custom-domain' | 'query-param' | null
 *
 * Examples:
 * - URL: https://acme.tendo.one/supplier => { slug: 'acme', type: 'subdomain' }
 * - URL: https://supplier.acme-corp.com => { customDomain: 'acme-corp.com', type: 'custom-domain' }
 * - URL: http://localhost:5173?tenant=acme => { slug: 'acme', type: 'query-param' }
 */
export interface TenantIdentification {
  slug: string | null;
  customDomain: string | null;
  type: 'subdomain' | 'custom-domain' | 'query-param' | null;
}

export const getTenantIdentification = (): TenantIdentification => {
  if (typeof window === 'undefined') {
    return { slug: null, customDomain: null, type: null };
  }

  const hostname = window.location.hostname;
  const baseDomain = config.tenant.domain;

  // Check if it's a custom domain first
  if (isCustomDomain(hostname, baseDomain)) {
    const customDomain = extractCustomDomain(hostname);
    return {
      slug: null,
      customDomain,
      type: 'custom-domain',
    };
  }

  // Try to extract from hostname (app domain subdomain)
  const subdomain = extractSubdomain(hostname, baseDomain);
  if (subdomain) {
    return {
      slug: subdomain,
      customDomain: null,
      type: 'subdomain',
    };
  }

  // Fallback to query parameter for local development
  const urlParams = new URLSearchParams(window.location.search);
  const tenantParam = urlParams.get('tenant');

  if (tenantParam) {
    return {
      slug: tenantParam,
      customDomain: null,
      type: 'query-param',
    };
  }

  return { slug: null, customDomain: null, type: null };
};

/**
 * Gets the subdomain from the current window location
 * Falls back to query parameter 'tenant' for local development
 * @returns The subdomain/tenant slug or null
 *
 * @deprecated Use getTenantIdentification() for full tenant info
 *
 * Examples:
 * - URL: https://acme.tendo.one/dashboard => 'acme'
 * - URL: http://localhost:5173?tenant=acme => 'acme'
 * - URL: http://localhost:5173 => null
 */
export const getCurrentSubdomain = (): string | null => {
  const tenant = getTenantIdentification();
  return tenant.slug;
};

/**
 * Builds a URL with a specific subdomain
 * @param subdomain - The subdomain to use
 * @param path - The path to append (default: '/')
 * @param preserveQuery - Whether to preserve current query params (default: false)
 * @returns The full URL with subdomain
 *
 * Examples:
 * - buildSubdomainUrl('acme', '/dashboard') => 'https://acme.tendo.one/dashboard'
 * - buildSubdomainUrl('demo', '/products', true) => 'https://demo.tendo.one/products?existing=params'
 */
export const buildSubdomainUrl = (
  subdomain: string,
  path: string = '/',
  preserveQuery: boolean = false
): string => {
  const baseDomain = config.tenant.domain;
  const protocol = window.location.protocol;
  const isLocal =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.');

  let url: string;

  if (isLocal) {
    // For local development, use query parameter
    const port = window.location.port ? `:${window.location.port}` : '';
    url = `${protocol}//${window.location.hostname}${port}${path}?tenant=${subdomain}`;
  } else {
    // For production, use subdomain
    url = `${protocol}//${subdomain}.${baseDomain}${path}`;
  }

  // Preserve existing query parameters if requested
  if (preserveQuery && window.location.search) {
    const separator = url.includes('?') ? '&' : '?';
    const currentParams = window.location.search.substring(1);
    url = `${url}${separator}${currentParams}`;
  }

  return url;
};

/**
 * Validates if a subdomain is valid (alphanumeric and hyphens only)
 * @param subdomain - The subdomain to validate
 * @returns true if valid, false otherwise
 *
 * Rules:
 * - Must be 1-63 characters
 * - Can only contain lowercase letters, numbers, and hyphens
 * - Cannot start or end with a hyphen
 * - Cannot contain consecutive hyphens
 */
export const isValidSubdomain = (subdomain: string): boolean => {
  if (!subdomain || subdomain.length === 0 || subdomain.length > 63) {
    return false;
  }

  // Regex: lowercase letters, numbers, hyphens (not at start/end, not consecutive)
  const subdomainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

  if (!subdomainRegex.test(subdomain)) {
    return false;
  }

  // Check for consecutive hyphens
  if (subdomain.includes('--')) {
    return false;
  }

  return true;
};

/**
 * Generates a slug from a name suitable for use as a subdomain
 * @param name - The organization name
 * @returns A URL-safe slug
 *
 * Examples:
 * - generateSlugFromName('Acme Corp') => 'acme-corp'
 * - generateSlugFromName('My Company 123!') => 'my-company-123'
 * - generateSlugFromName('  Test  ') => 'test'
 */
export const generateSlugFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace consecutive hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Navigates to a different tenant/organization
 * @param tenantSlug - The slug of the tenant to switch to
 * @param path - The path to navigate to (default: '/')
 */
export const navigateToTenant = (
  tenantSlug: string,
  path: string = '/'
): void => {
  const url = buildSubdomainUrl(tenantSlug, path);
  window.location.href = url;
};

/**
 * Checks if the current environment is local development
 * @returns true if running locally, false otherwise
 */
export const isLocalDevelopment = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.endsWith('.local')
  );
};

/**
 * Determines the correct router basepath based on the current URL pattern.
 * - App domain (e.g. acme.tendo.one/supplier): '/supplier'
 * - Custom domain (e.g. supplier.acme-corp.com): '/'
 * - Local development: '/'
 */
export const getBasePath = (): string => {
  if (typeof window === 'undefined') return '/';

  const hostname = window.location.hostname;
  const baseDomain = config.tenant.domain;

  if (isLocalDevelopment()) return '/';
  if (isCustomDomain(hostname, baseDomain)) return '/';

  return '/supplier';
};

/**
 * Gets the base app URL without subdomain
 * @returns The base URL
 */
export const getBaseUrl = (): string => {
  const protocol = window.location.protocol;
  const baseDomain = config.tenant.domain;
  const isLocal = isLocalDevelopment();

  if (isLocal) {
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${protocol}//${window.location.hostname}${port}`;
  }

  return `${protocol}//${baseDomain}`;
};
